import React, { useContext, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { TextField, Typography, Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { SingleAutoCompleteSelectChip } from "@/modules/common/components/input/single-autocomplete-select";
import { createOptions, getReferralLabel } from "@/modules/common/utils";
import MoneyInput from "@/modules/common/components/input/money-input";
import useBlockedForm from "@/modules/lead-page/hooks/lead-info/status/forms/blocked-form.hook";
import FormFooter from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-footer";
import { SaleType, StaticFormProps } from "@/modules/lead-page/types";
import useLocationsData from "@/modules/common/hooks/locations-data.hook";
import { LocationDataParams, Options } from "@/modules/common/types";
import SingleSelectSearch from "@/modules/common/components/input/single-autocomplete-search";
import { LocationsResponse } from "@/modules/common/dtos/index.v2";
import ReadOnlyTextInput from "@/modules/common/components/input/readonly-text-input";
import Grid from "@mui/material/GridLegacy";
import { useManageReferrals } from "@/modules/lead-page/hooks/manage-referrals.hook";

const BlockedForm: React.FC<StaticFormProps> = ({
  handleSaleSubmission,
  saleType,
  handleClose,
  sale,
  isLoadingSubmition,
}) => {
  const { lead } = useContext(LeadPageContext);

  const { referralsData } = useManageReferrals();
  const leadHasActiveReferral = referralsData?.data?.find(
    (referral) => referral.isExpired === false
  );

  const { PropertyTypeData } = useLocationsData();
  const [selectedUnitLocation, setSelectedUnitLocation] = useState<Options>();

  const {
    errors,
    control,
    onSubmit,
    handleSubmit,
    selectedCompound,
    selectedPropertyType,
  } = useBlockedForm({
    selectedUnitLocation,
    handleSaleSubmission,
    saleType,
    existingSale: sale,
  });

  const today = new Date();
  const minDate = new Date(today);
  minDate.setDate(today.getDate() - 1);
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + 2);
  const maxContractDate = new Date(today);
  maxContractDate.setFullYear(today.getFullYear() + 1);

  const {
    locationsData: compoundsData,
    locationsLoading: compoundsLoading,
    locationSearchTerm: compoundsSearchTerm,
    handleSearchChange: handleCompoundSearchChange,
  } = useLocationsData({
    locationDataParams: LocationDataParams.COMPOUND,
    ...(saleType === SaleType.EOI && { isLaunch: true }),
  });

  const compoundOptions = useMemo(() => {
    const mapCompoundToOption = (
      location: LocationsResponse["data"]["results"][number]
    ) => ({
      value: location?.compound?.uid,
      optionValueUid: location?.compound?.area?.uid,
      label: `${location?.compound?.name} - ${location?.compound?.area?.name}`,
      developerName: location?.compound?.developer?.name ?? "",
    });
    const baseOptions = (compoundsData?.results || []).map(mapCompoundToOption);

    const saleCompoundOption = sale?.compound
      ? {
          value: sale.compound.uid,
          optionValueUid: sale.area?.uid,
          label: `${sale.compound.name} - ${sale.area?.name}`,
          developerName: sale.compound.developer?.name ?? "",
        }
      : null;

    return saleCompoundOption &&
      !baseOptions.find((opt) => opt.value === saleCompoundOption.value)
      ? [...baseOptions, saleCompoundOption]
      : baseOptions;
  }, [compoundsData?.results, sale?.compound, sale?.area]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h3_med">
          {sale ? "Edit" : "New"} {saleType} Form
        </Typography>
        <Typography variant="h4_med">
          {getReferralLabel({ sale, leadHasActiveReferral })}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item mobile={12} tablet={6}>
          <SingleSelectSearch
            value={sale?.compound?.uid || selectedCompound}
            size="medium"
            options={compoundOptions}
            isLoading={compoundsLoading}
            searchValue={compoundsSearchTerm}
            onSearchValueChange={handleCompoundSearchChange}
            name="compoundUId"
            control={control}
            label="Compound"
            error={errors.compoundUId?.message}
            onValueChange={(value) => {
              const selectedOption = compoundOptions.find(
                (opt) => opt.value === value
              );
              setSelectedUnitLocation(selectedOption);
            }}
          />
        </Grid>
        <Grid item mobile={12} tablet={6}>
          <ReadOnlyTextInput
            label="Developer"
            value={
              selectedUnitLocation?.developerName ||
              sale?.compound?.developer?.name ||
              ""
            }
          />
        </Grid>
        <Grid item mobile={12} tablet={6}>
          <SingleAutoCompleteSelectChip
            name="propertyTypeUId"
            control={control}
            value={selectedPropertyType || sale?.propertyType?.uid}
            options={
              createOptions({
                list: PropertyTypeData,
                field1: "uid",
                field2: "name",
              }) || []
            }
            label="Property Type"
            error={errors.propertyTypeUId?.message}
          />
        </Grid>
        <Grid item mobile={12} tablet={6}>
          <Controller
            name="dateOfType"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={`Date of ${saleType}`}
                  value={field.value}
                  onChange={(newValue: Date | null) => {
                    const adjustedDate = newValue
                      ? new Date(newValue.setHours(12, 0, 0, 0))
                      : null;
                    field.onChange(adjustedDate);
                  }}
                  minDate={minDate}
                  maxDate={maxDate}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.dateOfType,
                      helperText: errors.dateOfType?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6}>
          <Controller
            name="expectedContractionDate"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expected Contraction Date"
                  value={field.value}
                  onChange={(newValue: Date | null) => {
                    const adjustedDate = newValue
                      ? new Date(newValue.setHours(12, 0, 0, 0))
                      : null;
                    field.onChange(adjustedDate);
                  }}
                  maxDate={maxContractDate}
                  format="dd/MM/yyyy"
                  minDate={today}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.expectedContractionDate,
                      helperText: errors.expectedContractionDate?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6}>
          <Controller
            name="expectedValue"
            control={control}
            render={({ field }) => (
              <MoneyInput
                label="Expected Value"
                onChange={(e) => field.onChange(e.target.value)}
                defaultValue={field.value}
                isError={!!errors.expectedValue}
                helperText={errors.expectedValue?.message}
              />
            )}
          />
        </Grid>
        {lead?.data?.lead?.lead_type_id === 4 && (
          <Grid item mobile={12} tablet={6}>
            <Controller
              name="cashPrice"
              control={control}
              render={({ field }) => (
                <MoneyInput
                  label="Cash Price"
                  onChange={(e) => field.onChange(e.target.value)}
                  defaultValue={field.value ? field.value : undefined}
                  isError={!!errors.cashPrice}
                  helperText={errors.cashPrice?.message}
                />
              )}
            />
          </Grid>
        )}
        <Grid item mobile={12}>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                label="Comment"
                multiline
                rows={4}
                error={!!errors.comment}
                helperText={errors.comment?.message}
                inputProps={{ minLength: 4 }} // Added minLength attribute
              />
            )}
          />
        </Grid>
        <FormFooter
          handleClose={handleClose}
          isLoadingSubmition={isLoadingSubmition}
          confirmText={sale ? "Update" : "Save"}
        />
      </Grid>
    </form>
  );
};

export default BlockedForm;
